import { useMemo, useState } from "react";
import { ResultsTable, ResultsCards } from "./ResultsTable";
import { RosterState } from "./RosterState";
import { Podium } from "./Podium";
import { PlayerPanel } from "./PlayerPanel";
import { CountUp } from "./CountUp";
import { money, computeSeasonBadges, rosterFromRounds, computeRecords } from "@/lib/scoring";
import { WEEKLY_LINE } from "@/data/weeklyLine";
import { RecordsStrip } from "./RecordsStrip";

const Tile = ({ label, testId, children }) => (
  <div className="rounded-lg border border-cypress-gold/20 bg-cypress-green/40 px-4 py-3" data-testid={testId}>
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cypress-cream/50">{label}</p>
    <p className="mt-1 font-mono text-xl font-bold text-cypress-gold sm:text-2xl">{children}</p>
  </div>
);

export const Leaderboard = ({ rounds, ytd }) => {
  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState("totalWon");
  const [sortAsc, setSortAsc] = useState(false);
  const seasonBadges = useMemo(() => computeSeasonBadges(rounds), [rounds]);
  const records = useMemo(() => computeRecords(rounds), [rounds]);

  const handleSort = (col) => {
    if (col === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(col);
      setSortAsc(false);
    }
  };

  if (rounds.length === 0) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-cypress-gold">Leaderboard</p>
        <RosterState names={rosterFromRounds(rounds)} />
      </div>
    );
  }

  const players = ytd.players;
  const val = (p) => (sortKey === "rounds" ? p.rounds : sortKey === "perRound" ? (p.rounds ? p.totalWon / p.rounds : 0) : p.totalWon);
  // Display only: YTD board shows the top 10 by net; everyone else's
  // data stays intact for totals, badges and player panels.
  const topByNet = [...players].sort((a, b) => b.totalWon - a.totalWon).slice(0, 10);
  const tablePlayers = [...topByNet].sort((a, b) => (sortAsc ? val(a) - val(b) : val(b) - val(a)));
  const maxDots = Math.max(0, ...players.map((p) => p.dots));
  const badgesFor = (p) => {
    const set = new Set(seasonBadges.get(p.name) || []);
    if (maxDots > 0 && p.dots === maxDots) set.add("mostDots");
    return [...set];
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-cypress-gold">Leaderboard</p>
        <h2 className="mt-1 text-2xl font-bold uppercase tracking-wide sm:text-3xl" data-testid="leaderboard-context-label">
          YTD Money Leader
        </h2>
        {WEEKLY_LINE?.trim() && (
          <p className="mt-2 max-w-2xl text-sm italic leading-relaxed text-cypress-cream/60" data-testid="weekly-line">
            {WEEKLY_LINE}
          </p>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="leaderboard-tiles">
        <Tile label="Rounds Played" testId="tile-rounds-played"><CountUp value={rounds.length} format={(v) => String(Math.round(v))} /></Tile>
        <Tile label="Players" testId="tile-players"><CountUp value={ytd.playerCount} format={(v) => String(Math.round(v))} /></Tile>
        <Tile label="Total Paid Out" testId="tile-total-paid-out"><CountUp value={ytd.totalPot} format={(v) => money(v)} /></Tile>
        <Tile label="Total Dots" testId="tile-total-dots"><CountUp value={ytd.totalDots} format={(v) => String(Math.round(v))} /></Tile>
      </div>

      <div className="animate-fade-up">
        <Podium players={tablePlayers} badgesFor={badgesFor} onSelect={setSelected} />

        <div className="mt-6 hidden rounded-xl border border-cypress-gold/20 bg-cypress-green/30 p-4 md:block">
          <ResultsTable
            players={tablePlayers}
            testIdPrefix="leaderboard"
            badgesFor={badgesFor}
            onSelect={setSelected}
            ytdColumns
            sortKey={sortKey}
            sortAsc={sortAsc}
            onSort={handleSort}
          />
        </div>

        <div className="mt-6 md:hidden">
          <ResultsCards players={tablePlayers} testIdPrefix="leaderboard" badgesFor={badgesFor} />
        </div>

        <p className="mt-4 text-center text-xs text-cypress-cream/40" data-testid="season-player-count">
          {ytd.playerCount} players have played this season.
        </p>
        <p className="mt-4 text-center text-xs text-cypress-cream/40" data-testid="net-note">
          All figures are net. The $35 buy-in is already subtracted.
        </p>
        <p className="mt-1 text-center text-xs text-cypress-cream/40" data-testid="rounding-note">
          Due to rounding, the dollar amounts can be off up or down about a buck.
        </p>
        <RecordsStrip records={records} />
      </div>

      {selected && <PlayerPanel name={selected} rounds={rounds} onClose={() => setSelected(null)} />}
    </div>
  );
};
